import React, { useState } from "react";
import { usePFDataContext } from "../context/PfContext";

import ProductFilters from "./ProductFilters";
import BrandAccordion from "./BrandAccordion";
import BrandCtas from "./BrandCtas";
import JumpToNextBrand from "./JumpToNextBrand";
import BrandHeader from "./BrandHeader";
import ProductTableRow from "./ProductTableRow";
import BrandCategoryHeader from "./BrandCategoryHeader";


const ProductList = () => {
    const { pfData, updatePfData } = usePFDataContext();
    const brands = pfData.pfProductBrands;
    const products = pfData.filteredpfProducts;

    // Sorting State (Stores the current sorting column and order)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    // Define table headers and their corresponding product keys
    const columnHeaders = [
        { label: "Category", key: "inventory_category" },
        ...(pfData.pfView === "private" ? [
            { label: "Available", key: "available" },
            { label: "Expiration Date", key: "batch_expiration_date" },
            { label: "Batch", key: "batch_code"}
        ] : []),
        { label: "Strain", key: "strain" },
        { label: "Name", key: "sku_name" },
        { label: "Weight", key: "weight" },
        { label: "Thc", key: "thc_percentage" },
        { label: "Case", key: "case_pack_size" },
        { label: "Unit Price", key: "price_per_unit" },
        { label: "Total Price", key: "totalPrice" }, // Special computed field
    ];

    // Function to handle sorting when a column header is clicked
    const handleSort = (brandName, categoryName, key) => {
        setSortConfig((prev) => {
            const newDirection = prev.key === key && prev.direction === "asc" ? "desc" : "asc";

            // Clone products to avoid mutating state directly
            const updatedProducts = { ...pfData.filteredpfProducts };

            if (
                updatedProducts[brandName] &&
                updatedProducts[brandName].categories &&
                updatedProducts[brandName].categories[categoryName]
            ) {
                updatedProducts[brandName].categories[categoryName] = [...updatedProducts[brandName].categories[categoryName]].sort(
                    (a, b) => {
                        let aValue = a[key];
                        let bValue = b[key];

                        // Compute total price if sorting by total-price
                        if (key === "totalPrice") {
                            aValue = a.price_per_unit * a.case_pack_size;
                            bValue = b.price_per_unit * b.case_pack_size;
                        }

                        // Handle both numbers and strings
                        if (!isNaN(aValue) && !isNaN(bValue)) {
                            return newDirection === "asc" ? aValue - bValue : bValue - aValue;
                        } else {
                            return newDirection === "asc"
                                ? String(aValue).localeCompare(String(bValue), undefined, { numeric: true })
                                : String(bValue).localeCompare(String(aValue), undefined, { numeric: true });
                        }
                    }
                );
            }

            // Update the context with sorted products
            updatePfData("filteredpfProducts", updatedProducts);

            return { key, direction: newDirection };
        });
    };

    return (
        <>
            <ProductFilters />
            {brands.map((brandName) => {
                const brandObj = products[brandName];
                if (!brandObj) return null;

                return (
                    <div key={brandName} className="brand__container" data-brand={brandName.toLowerCase()} id={brandName.toLowerCase()}>
                        <BrandHeader brandObj={brandObj} brandName={brandName} />

                        {brandObj?.metaData?.brand_ctas?.length > 0 && <BrandCtas ctas={brandObj.metaData.brand_ctas} />}
                        <JumpToNextBrand brandName={brandName} />

                        {brandObj?.metaData?.brand_accordion?.accordion_title && (
                            <BrandAccordion title={brandObj.metaData.brand_accordion.accordion_title} content={brandObj.metaData.brand_accordion.accordion_content} />
                        )}

                        {Object.entries(brandObj.categories).map(([categoryName, categoryProducts]) => (
                            <div key={categoryName} className="brand__category" data-category={categoryName} data-parent-brand={brandName.toLowerCase()}>
                                <BrandCategoryHeader brandName={brandName} categoryName={categoryName} />

                                {/* Product List Table */}
                                <div className="product__list-table ">
                                    {/* Product Table Header */}
                                    <div className="product__list-head product__list-head--private">
                                        {columnHeaders.map(({ label, key }) => (
                                            <div
                                                key={key}
                                                className={`product__list-head--${label.toLowerCase().replace(" ", "-")}`} // Convert label to class name
                                                onClick={() => handleSort(brandName, categoryName, key)}
                                                style={{ cursor: "pointer", textTransform: "capitalize" }}
                                            >
                                                {label}
                                                {(key === "price_per_unit" || key === "totalPrice") ? ' $' : ''}
                                                
                                                
                                                {sortConfig.key === key ? (
                                                    sortConfig.direction === "asc" ? <span className="order">↑</span> : <span className="order">↓</span>
                                                ) : null
                                                }

                                              
                                            </div>
                                        ))}
                                        <div className="product__list-head--cart"></div>
                                    </div>

                                    {/* Product Rows */}
                                    {categoryProducts.map((product) => (
                                        <ProductTableRow key={product.id} product={product} categoryName={categoryName} brandName={brandName} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })}
        </>
    );
};

export default ProductList;
